import type { PricingKey } from '@/config/pricing';

// Contenido de las landings de Google Ads (/lp/...): noindex, sin menú, formulario arriba.

export type LandingLocale = 'es' | 'en';
export type LandingKey = 'nomada-es' | 'nomada-en' | 'beckham-es' | 'beckham-en';
export type LandingServiceKey = 'nomada' | 'beckham' | 'pack' | 'otro';
export type MoveTimelineKey = 'lt3' | '3to6' | 'gt6' | 'already';

type Localized = { es: string; en: string };

/** Service select of the landing form. `servicio` is the slug sent to /contacto/gracias?servicio=. */
export const LANDING_SERVICE_OPTIONS: Record<
  LandingServiceKey,
  Localized & { servicio: string; serviceId: string; inquiry: string }
> = {
  nomada: {
    es: 'Nómada digital',
    en: 'Digital nomad visa',
    servicio: 'visado-nomada-digital',
    serviceId: 'visado-de-nomada-digital',
    inquiry: 'Visado de nómada digital',
  },
  beckham: {
    es: 'Ley Beckham',
    en: 'Beckham Law',
    servicio: 'ley-beckham',
    serviceId: 'ley-beckham',
    inquiry: 'Ley Beckham (régimen de impatriados)',
  },
  pack: {
    es: 'Pack Nómada + Beckham',
    en: 'Digital nomad + Beckham Law package',
    servicio: 'pack-nomada-digital-ley-beckham',
    serviceId: 'pack-nomada-digital-ley-beckham',
    inquiry: 'Pack Nómada digital + Ley Beckham',
  },
  otro: {
    es: 'Otro',
    en: 'Other',
    servicio: 'otro',
    serviceId: 'consulta-juridica',
    inquiry: 'Otra consulta (landing)',
  },
};

export const MOVE_TIMELINE_OPTIONS: Record<MoveTimelineKey, Localized> = {
  lt3: { es: 'Menos de 3 meses', en: 'Less than 3 months' },
  '3to6': { es: '3–6 meses', en: '3–6 months' },
  gt6: { es: 'Más de 6 meses', en: 'More than 6 months' },
  already: { es: 'Ya vivo en España', en: 'I already live in Spain' },
};

export type LandingContent = {
  key: LandingKey;
  locale: LandingLocale;
  path: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  subtitle: string;
  defaultService: LandingServiceKey;
  pricingKey: PricingKey;
  pricingLabel: string;
  /** Context for the pre-filled WhatsApp message. */
  whatsappTopic: string;
  faq: Array<{ question: string; answer: string }>;
};

export const LANDING_SHARED_COPY: Record<
  LandingLocale,
  {
    subtitle: string;
    benefitsTitle: string;
    benefits: [string, string, string];
    stepsTitle: string;
    steps: [string, string, string];
    feesTitle: string;
    feesNote: string;
    reviewsTitle: string;
    reviewsNote: string;
    faqTitle: string;
    finalTitle: string;
    finalSubtitle: string;
    whatsappCta: string;
    callLabel: string;
    legalLinks: Array<{ label: string; href: string }>;
    packCta: string;
  }
> = {
  es: {
    subtitle: 'Revisamos tu caso en 24 h y te damos un presupuesto cerrado.',
    benefitsTitle: 'Por qué MigrationLaw',
    benefits: [
      'Abogados colegiados en Madrid (ICAM).',
      'Presupuesto cerrado, sin sorpresas.',
      'Visado y fiscalidad en el mismo despacho.',
    ],
    stepsTitle: 'Cómo trabajamos',
    steps: [
      'Revisión de viabilidad de tu caso en 24 h.',
      'Preparamos y presentamos tu expediente.',
      'Seguimiento hasta la resolución (y alta en la Ley Beckham si aplica).',
    ],
    feesTitle: 'Honorarios',
    feesNote: 'Honorarios orientativos, IVA no incluido. Tras revisar tu caso te damos un precio cerrado por escrito. Las tasas administrativas se pagan aparte.',
    reviewsTitle: 'Opiniones reales en Trustpilot',
    reviewsNote: 'Extractos literales de opiniones publicadas en Trustpilot.',
    faqTitle: 'Preguntas frecuentes',
    finalTitle: '¿Empezamos con tu caso?',
    finalSubtitle: 'Déjanos tus datos y un abogado te responde en menos de 24 h.',
    whatsappCta: 'Escríbenos por WhatsApp',
    callLabel: 'Llamar',
    legalLinks: [
      { label: 'Aviso legal y privacidad', href: '/privacidad' },
      { label: 'Cookies', href: '/cookies' },
    ],
    packCta: 'Ver el pack Nómada digital + Ley Beckham →',
  },
  en: {
    subtitle: 'We review your case within 24h and give you a fixed quote.',
    benefitsTitle: 'Why MigrationLaw',
    benefits: [
      'Lawyers registered with the Madrid Bar (ICAM).',
      'Fixed quote, no surprises.',
      'Visa and tax handled by the same firm.',
    ],
    stepsTitle: 'How we work',
    steps: [
      'Feasibility review of your case within 24h.',
      'We prepare and file your application.',
      'Follow-up until the decision (and Beckham Law registration if it applies).',
    ],
    feesTitle: 'Fees',
    feesNote: 'Indicative fees, VAT not included. After reviewing your case we give you a fixed price in writing. Government fees are paid separately.',
    reviewsTitle: 'Real reviews on Trustpilot',
    reviewsNote: 'Verbatim excerpts from reviews published on Trustpilot (original language: Spanish).',
    faqTitle: 'Frequently asked questions',
    finalTitle: 'Shall we start with your case?',
    finalSubtitle: 'Leave your details and a lawyer will reply within 24h.',
    whatsappCta: 'Message us on WhatsApp',
    callLabel: 'Call',
    legalLinks: [
      { label: 'Legal notice & privacy', href: '/en/privacidad' },
      { label: 'Cookies', href: '/en/cookies' },
    ],
    packCta: 'See the Digital nomad + Beckham Law package →',
  },
};

const NOMAD_FAQ_ES = [
  {
    question: '¿Cuánto tarda el visado de nómada digital?',
    answer:
      'Si lo pides desde España como residencia, la Administración tiene 20 días hábiles para resolver. Desde el consulado, el plazo habitual es de 10 días hábiles. Preparar bien la documentación suele llevar de 2 a 4 semanas.',
  },
  {
    question: '¿Qué ingresos mínimos necesito?',
    answer:
      'Debes acreditar al menos el 200% del salario mínimo interprofesional mensual, más un 75% del SMI por el primer familiar y un 25% por cada familiar adicional. Te confirmamos la cifra vigente al revisar tu caso.',
  },
  {
    question: '¿Puedo traer a mi familia?',
    answer: 'Sí. Tu cónyuge o pareja y tus hijos a cargo pueden solicitar la residencia contigo, acreditando los ingresos adicionales.',
  },
  {
    question: '¿Puedo pedir la Ley Beckham a la vez?',
    answer:
      'Sí. Los nómadas digitales pueden acogerse al régimen de impatriados si no han sido residentes fiscales en España en los 5 años anteriores. Hay que solicitarlo en los 6 meses siguientes al alta en la Seguridad Social.',
  },
  {
    question: '¿Qué pasa si me lo deniegan?',
    answer:
      'Analizamos la resolución y presentamos recurso o una nueva solicitud corrigiendo el motivo de la denegación.',
  },
];

const NOMAD_FAQ_EN = [
  {
    question: 'How long does the digital nomad visa take?',
    answer:
      'If you apply from Spain for residence, the authorities have 20 working days to decide. From a consulate, the usual timeframe is 10 working days. Preparing the documents properly usually takes 2 to 4 weeks.',
  },
  {
    question: 'What is the minimum income?',
    answer:
      'You must prove at least 200% of the Spanish monthly minimum wage, plus 75% of it for the first family member and 25% for each additional one. We confirm the current figure when we review your case.',
  },
  {
    question: 'Can I bring my family?',
    answer: 'Yes. Your spouse or partner and dependent children can apply for residence with you, provided you prove the additional income.',
  },
  {
    question: 'Can I apply for the Beckham Law at the same time?',
    answer:
      'Yes. Digital nomads can opt into the impatriate regime if they have not been Spanish tax residents in the previous 5 years. You must apply within 6 months of registering with Social Security.',
  },
  {
    question: 'What happens if my application is refused?',
    answer:
      'We review the decision and file an appeal or a new application fixing the reason for the refusal.',
  },
];

const BECKHAM_FAQ_ES = [
  {
    question: '¿Cuál es el plazo para pedir la Ley Beckham?',
    answer:
      'Seis meses desde tu alta en la Seguridad Social en España (o desde el inicio de la actividad que consta en tu visado). Pasado ese plazo ya no es posible acogerse, por eso conviene preparar la solicitud (modelo 149) antes de llegar.',
  },
  {
    question: '¿Hay unos ingresos mínimos?',
    answer:
      'La Ley Beckham no exige ingresos mínimos: tributas al 24% hasta 600.000 € de rendimientos del trabajo. Si llegas con el visado de nómada digital, ese visado sí exige acreditar ingresos mínimos.',
  },
  {
    question: '¿Puede aplicarse a mis familiares?',
    answer:
      'Sí, en determinados casos el cónyuge y los hijos menores de 25 años que se desplacen contigo pueden acogerse también al régimen. Lo revisamos en la simulación fiscal.',
  },
  {
    question: '¿Puedo pedirla junto al visado de nómada digital?',
    answer:
      'Sí. Desde la Ley de Startups los teletrabajadores con visado de nómada digital pueden acogerse. Nuestro pack coordina el visado y la solicitud del régimen.',
  },
  {
    question: '¿Qué pasa si Hacienda la deniega?',
    answer:
      'Presentamos alegaciones o recurso frente a la resolución. Revisar antes los requisitos (residencia previa, tipo de traslado, plazo) es la mejor forma de evitarlo.',
  },
];

const BECKHAM_FAQ_EN = [
  {
    question: 'What is the deadline to apply for the Beckham Law?',
    answer:
      'Six months from your Social Security registration in Spain (or from the start of the activity shown on your visa). After that you can no longer opt in, so it is best to prepare the application (form 149) before you arrive.',
  },
  {
    question: 'Is there a minimum income?',
    answer:
      'The Beckham Law has no minimum income: you pay 24% on employment income up to EUR 600,000. If you arrive with the digital nomad visa, that visa does require proof of minimum income.',
  },
  {
    question: 'Can it apply to my family?',
    answer:
      'Yes, in some cases your spouse and children under 25 who move with you can also opt into the regime. We check this in the tax simulation.',
  },
  {
    question: 'Can I apply together with the digital nomad visa?',
    answer:
      'Yes. Since Spain’s Startups Law, remote workers with a digital nomad visa can opt in. Our package coordinates the visa and the tax regime application.',
  },
  {
    question: 'What if the tax office refuses it?',
    answer:
      'We file submissions or an appeal against the decision. Checking the requirements beforehand (prior residence, type of move, deadline) is the best way to avoid it.',
  },
];

export const LANDING_PAGES: Record<LandingKey, LandingContent> = {
  'nomada-es': {
    key: 'nomada-es',
    locale: 'es',
    path: '/lp/nomada-digital',
    metaTitle: 'Visado de nómada digital en España con abogados | MigrationLaw',
    metaDescription: 'Abogados colegiados en Madrid. Revisamos tu caso de visado de nómada digital en 24 h y te damos un presupuesto cerrado.',
    h1: 'Visado de nómada digital en España, con abogados',
    subtitle: LANDING_SHARED_COPY.es.subtitle,
    defaultService: 'nomada',
    pricingKey: 'NOMAD_RANGE',
    pricingLabel: 'Visado de nómada digital',
    whatsappTopic: 'visado nómada digital',
    faq: NOMAD_FAQ_ES,
  },
  'nomada-en': {
    key: 'nomada-en',
    locale: 'en',
    path: '/en/lp/digital-nomad-visa',
    metaTitle: 'Spain Digital Nomad Visa handled by immigration lawyers | MigrationLaw',
    metaDescription: 'Registered lawyers in Madrid. We review your Spain digital nomad visa case within 24h and give you a fixed quote.',
    h1: 'Spain Digital Nomad Visa — handled by immigration lawyers',
    subtitle: LANDING_SHARED_COPY.en.subtitle,
    defaultService: 'nomada',
    pricingKey: 'NOMAD_RANGE',
    pricingLabel: 'Digital nomad visa',
    whatsappTopic: 'digital nomad visa',
    faq: NOMAD_FAQ_EN,
  },
  'beckham-es': {
    key: 'beckham-es',
    locale: 'es',
    path: '/lp/ley-beckham',
    metaTitle: 'Ley Beckham: tributa al 24 % con abogados | MigrationLaw',
    metaDescription: 'Solicitud del régimen de impatriados (Ley Beckham) con abogados colegiados. Revisamos tu caso en 24 h con presupuesto cerrado.',
    h1: 'Ley Beckham: tributa al 24 %',
    subtitle: LANDING_SHARED_COPY.es.subtitle,
    defaultService: 'beckham',
    pricingKey: 'BECKHAM_RANGE',
    pricingLabel: 'Solicitud de la Ley Beckham',
    whatsappTopic: 'Ley Beckham',
    faq: BECKHAM_FAQ_ES,
  },
  'beckham-en': {
    key: 'beckham-en',
    locale: 'en',
    path: '/en/lp/beckham-law',
    metaTitle: 'Beckham Law Spain: pay 24% flat tax | MigrationLaw',
    metaDescription: 'Beckham Law (impatriate regime) application handled by registered lawyers. Case review within 24h and a fixed quote.',
    h1: 'Beckham Law Spain: pay 24% flat tax',
    subtitle: LANDING_SHARED_COPY.en.subtitle,
    defaultService: 'beckham',
    pricingKey: 'BECKHAM_RANGE',
    pricingLabel: 'Beckham Law application',
    whatsappTopic: 'Beckham Law',
    faq: BECKHAM_FAQ_EN,
  },
};

/** Ads landing paths: noindex and excluded from the sitemap. */
export const LANDING_PATHS = Object.values(LANDING_PAGES).map((page) => page.path);
